<template>
  <div class="password-recovery">
    <!-- Form -->
    <form
      v-if="!success"
      class="recover-wrapper"
      novalidate
      test-id="form"
      @submit.prevent="requestPassword"
    >
      <div class="upper">
        <h1 class="recover-title">Recover Password</h1>
        <p>Enter the email address for your account:</p>
        <div class="field-wrapper">
          <LsInput
            v-model="email"
            name="email"
            track-id="password_recovery_view-email"
            placeholder="Email Address"
            type="email"
            :validation="{ required: true, email: true }"
          />
        </div>
      </div>

      <div class="middle">
        <RouterLink :to="{ name: 'login' }" class="back-to-signin" test-id="back_to_signin">
          <LsIcon type="chevron" size="md" class="chevron" />
          <span class="back-text has-text-weight-bold">back to sign in</span>
        </RouterLink>

        <LsButton
          text="send recover email"
          track-id="password_recovery_view-send_email"
          test-id="recover_button"
          type="submit"
          :loading="auth.loadingStatus.isUpdating"
        />
      </div>

      <div class="bottom">
        <span class="has-text-weight-bold not-registered">Not registered?</span>
        <a :href="signUpUrl" class="signup-link">Sign up and order service</a>
      </div>
    </form>
    <!-- Success -->
    <div v-else class="success-wrapper">
      <div class="text-wrapper">
        <h1 class="recover-title">Recover Password</h1>
        <p class="success-upper-text has-text-weight-bold success-text">
          An email has been sent to the email address supplied.
        </p>
        <p class="success-lower-text success-text">
          Please check your inbox for additional instructions.
        </p>
      </div>
      <div class="ok-button">
        <LsButton
          text="ok"
          track-id="password_recover_view-ok"
          test-id="ok_button"
          @click="returnToSignin"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { AUTH__REQUEST_PASSWORD } from '@/store/types';
import { LsButton, LsInput, LsIcon } from '@/components/common';
import { lsModalService, lsRouterHelpers } from 'ls-vue-common';
import { PasswordModal } from '@/modals';

export default {
  name: 'PasswordRecoveryView',
  components: {
    LsButton,
    LsInput,
    LsIcon,
  },
  $_veeValidate: {
    validator: 'new',
  },
  data() {
    return {
      email: '',
      signUpUrl: process.env.VUE_APP_SIGNUP_BASE_URL,
      success: false,
    };
  },
  computed: {
    ...mapState(['auth']),
  },
  created() {
    const tokenQuery = lsRouterHelpers.getQuery(this.$route, 'token');

    if (tokenQuery) {
      lsModalService.open({
        parent: this,
        component: PasswordModal,
        props: { type: 'reset', token: tokenQuery },
      });
      this.$router.replace({
        name: 'login',
      });
    }
  },
  methods: {
    ...mapActions([AUTH__REQUEST_PASSWORD]),
    async requestPassword() {
      const valid = await this.$validator.validateAll();
      if (!valid) {
        return;
      }

      this[AUTH__REQUEST_PASSWORD]({
        email: this.email,
        onSuccess: () => {
          this.success = true;
        },
      });
    },
    returnToSignin() {
      this.$router.push({
        name: 'login',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.recover-title {
  padding-bottom: 10px;
}

p {
  color: $primary;
  padding-bottom: 10px;
}

.field-wrapper {
  padding-top: 15px;
}

.middle {
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid $primary-light;
  padding: 72px 0 28px 0;
}

.back-to-signin {
  display: flex;
  align-items: center;

  &:hover {
    color: $special-primary-hover;
  }
  &:hover .chevron {
    opacity: 0.5;
  }
}

.chevron {
  transform: translateY(-1px) rotate(180deg);
}

.back-text {
  padding-left: 6px;
  text-transform: uppercase;
  text-align: left;
}

.bottom {
  color: $primary;
  display: flex;
  justify-content: center;
  padding-top: 46px;
}

.not-registered {
  padding-right: 4px;
}

.signup-link,
a {
  color: $primary;
  &:hover {
    color: $special-primary-hover;
  }
}

.success-wrapper {
  height: 340px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.success-title {
  padding: 30px 0;
}

.ok-button {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: $mobile-max-width) {
  .upper {
    padding-top: 10px;
  }

  h1 {
    text-align: center;
  }

  p {
    font-size: $size-5;
    text-align: center;
  }
  .middle {
    display: flex;
    flex-direction: column;
    padding: 69px 0 24px 0;
  }

  .back-to-signin {
    width: 100%;
    text-align: left;
    justify-content: flex-start;
    font-size: $size-6;
    padding-bottom: 16px;
  }

  .bottom {
    display: flex;
    padding-top: 32px;
  }

  .success-title {
    text-align: center;
  }

  .success-text {
    font-size: $size-6;
    padding: 6px 0;
    text-align: center;
  }
}

.themed-app {
  p,
  .bottom {
    color: $theme-color-primary;
  }

  .back-to-signin,
  .signup-link,
  a {
    color: $theme-color-primary;

    &:hover {
      opacity: 0.65;
    }

    &:hover .chevron {
      opacity: 1;
    }
  }

  .middle {
    border-color: $theme-color-primary-light;
  }
}
</style>
